/*
 * TwcGeo — the shared geolocation helper for the community pages.
 *
 * Two index pages ask the same question ("show me the ones near me"):
 * Clubhouses and Events. Both keep their own list markup, their own empty
 * states and their own words. Only the browser handshake and the distance
 * arithmetic are the same, so only those live here.
 *
 * This module has NO UI. It never writes to the DOM, never shows a message and
 * never remembers a permission. The host decides what a refusal looks like.
 *
 * Exposure follows assets/twc-cta-resolver.js: one IIFE, one global, idempotent
 * under duplicate script tags.
 *
 *   TwcGeo.isSupported()            -> boolean
 *   TwcGeo.requestPosition(opts)    -> Promise<{latitude, longitude, accuracy}>
 *                                      rejects with an Error carrying
 *                                      .code = 'unsupported' | 'denied'
 *                                             | 'unavailable' | 'timeout'
 *   TwcGeo.haversineKm(a, b)        -> number (great-circle km)
 *   TwcGeo.haversineSort(items, origin)
 *                                   -> NEW array, nearest first
 *
 * haversineSort() accepts DOM elements carrying `data-twc-lat` /
 * `data-twc-lng`, or plain objects with `lat`/`lng` (or `latitude`/
 * `longitude`). Every entry comes back as {item, distanceKm}; an entry with no
 * usable pair gets distanceKm === null and sorts to the end, because a
 * clubhouse with no coordinates must not disappear from the list.
 */
(function (global) {
  'use strict';

  if (global.TwcGeo) return; // idempotent under duplicate script tags

  var EARTH_RADIUS_KM = 6371;
  var DEFAULT_TIMEOUT_MS = 10000;

  /* ---- 1. the browser handshake ---------------------------------------- */

  function isSupported() {
    return !!(global.navigator && global.navigator.geolocation);
  }

  function geoError(code, message) {
    var err = new Error(message || code);
    err.code = code;
    return err;
  }

  /**
   * Ask the browser once. The permission prompt is the browser's, so this must
   * only ever run from a user gesture — a page that calls it on load trains
   * members to refuse.
   */
  function requestPosition(options) {
    var opts = options || {};

    if (!isSupported()) {
      return Promise.reject(geoError('unsupported', 'Geolocation is not available.'));
    }

    return new Promise(function (resolve, reject) {
      global.navigator.geolocation.getCurrentPosition(
        function (position) {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        function (error) {
          // 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT
          var code = 'unavailable';
          if (error && error.code === 1) code = 'denied';
          if (error && error.code === 3) code = 'timeout';
          reject(geoError(code, (error && error.message) || code));
        },
        {
          enableHighAccuracy: opts.enableHighAccuracy === true,
          timeout: typeof opts.timeout === 'number' ? opts.timeout : DEFAULT_TIMEOUT_MS,
          maximumAge: typeof opts.maximumAge === 'number' ? opts.maximumAge : 300000,
        }
      );
    });
  }

  /* ---- 2. distance ------------------------------------------------------ */

  function toNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    var n = typeof value === 'number' ? value : parseFloat(String(value).trim());
    return isFinite(n) ? n : null;
  }

  /** Read a {lat, lng} pair from a DOM element or a plain object. */
  function coordsOf(item) {
    if (!item) return null;

    var lat = null;
    var lng = null;

    if (item.nodeType === 1) {
      lat = toNumber(item.getAttribute('data-twc-lat'));
      lng = toNumber(item.getAttribute('data-twc-lng'));
    } else {
      lat = toNumber(item.lat !== undefined ? item.lat : item.latitude);
      lng = toNumber(item.lng !== undefined ? item.lng : item.longitude);
    }

    if (lat === null || lng === null) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { latitude: lat, longitude: lng };
  }

  function toRadians(degrees) {
    return (degrees * Math.PI) / 180;
  }

  function haversineKm(a, b) {
    var from = coordsOf(a);
    var to = coordsOf(b);
    if (!from || !to) return null;

    var dLat = toRadians(to.latitude - from.latitude);
    var dLng = toRadians(to.longitude - from.longitude);
    var lat1 = toRadians(from.latitude);
    var lat2 = toRadians(to.latitude);

    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);

    return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  /**
   * Nearest first. Returns a NEW array of {item, distanceKm} and never mutates
   * the input, so a host can keep its original order for the "all" tab.
   */
  function haversineSort(items, origin) {
    var list = Array.prototype.slice.call(items || []);
    var measured = list.map(function (item, index) {
      return { item: item, index: index, distanceKm: haversineKm(origin, item) };
    });

    measured.sort(function (a, b) {
      if (a.distanceKm === null && b.distanceKm === null) return a.index - b.index;
      if (a.distanceKm === null) return 1;
      if (b.distanceKm === null) return -1;
      if (a.distanceKm === b.distanceKm) return a.index - b.index;
      return a.distanceKm - b.distanceKm;
    });

    return measured.map(function (entry) {
      return { item: entry.item, distanceKm: entry.distanceKm };
    });
  }

  global.TwcGeo = {
    isSupported: isSupported,
    requestPosition: requestPosition,
    coordsOf: coordsOf,
    haversineKm: haversineKm,
    haversineSort: haversineSort,
  };
})(window);
